import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  ArrowDownIcon,
  ArrowUpIcon,
  CircleCheckIcon,
  PencilIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react";

import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { UserChooser } from "components/lib/Choosers";
import { describeItem, useEditSection } from "components/lib/EditToolbar";
import { describeRename } from "components/lib/EditToolbar/fields/organisation";

import type { PointOfContactRole } from "../../types";

type RoleEdit = Pick<PointOfContactRole, "id" | "name" | "description">;

const PointOfContact = () => {
  const [updatingRole, setUpdatingRole] = useState<RoleEdit | null>(null);
  const [roleEdits, setRoleEdits] = useState<Record<string, RoleEdit>>({});
  const user = useCurrentUser();
  const ownerOrganisation = user?.ownerOrganisation;
  const orgId = ownerOrganisation?._id;
  const { t } = useTranslation();

  const [savedRoles, setSavedRoles] = useState<PointOfContactRole[]>([]);
  const pointOfContactRoles = savedRoles.map((role) =>
    roleEdits[role.id] ? { ...role, ...roleEdits[role.id] } : role,
  );
  const editedRoles = savedRoles.filter((role) => roleEdits[role.id]);

  useEffect(() => {
    if (!orgId) return;
    api.organisations.getPointOfContactRoles(
      orgId,
      ({ pointOfContactRoles: newRoles }) => {
        setSavedRoles(newRoles);
      },
      () => {},
    );
  }, [orgId]);

  const roleItem = t("changes.organisation.pointOfContactRole");
  useEditSection({
    title: t("changes.organisation.title.pointOfContact"),
    changes: editedRoles.flatMap((role) => {
      const edit = roleEdits[role.id]!;
      const texts =
        edit.name !== role.name
          ? [describeRename(roleItem, role.name, edit.name)]
          : [];
      if (
        !texts.length ||
        (edit.description ?? "") !== (role.description ?? "")
      ) {
        texts.push(describeItem("edited", roleItem, edit.name));
      }
      return texts.map((text) => ({ text }));
    }),
    changed: editedRoles.length > 0,
    onSave: () => {
      if (!orgId) return;
      // One at a time, so each response holds every update before it
      const saveNext = ([edit, ...rest]: RoleEdit[]) => {
        if (!edit) return;
        api.organisations.updatePointOfContactRole(
          orgId,
          edit.id,
          { name: edit.name, description: edit.description ?? "" },
          ({ pointOfContactRoles: newRoles }) => {
            setSavedRoles(newRoles);
            setRoleEdits(({ [edit.id]: _saved, ...edits }) => edits);
            saveNext(rest);
          },
          () => {},
        );
      };
      saveNext(editedRoles.map((role) => roleEdits[role.id]!));
    },
    onDiscard: () => setRoleEdits({}),
  });

  const applyRoleEdit = () => {
    if (!updatingRole) return;
    const saved = savedRoles.find((role) => role.id === updatingRole.id);
    setRoleEdits(({ [updatingRole.id]: _previous, ...edits }) =>
      saved &&
      saved.name === updatingRole.name &&
      (saved.description ?? "") === (updatingRole.description ?? "")
        ? edits
        : { ...edits, [updatingRole.id]: updatingRole },
    );
    setUpdatingRole(null);
  };

  if (!orgId || !ownerOrganisation) {
    return null;
  }

  return (
    <Card fluid>
      <CardContent>
        <Modal open={!!updatingRole}>
          <ModalHeader>Update point of contact role</ModalHeader>
          <ModalContent>
            <H3>
              Name
              <Subheader>
                Give this point of contact a descriptive title. This will appear
                to any user seeking help and will make it easier for them to
                decide who to contact. Make sure this describes the nature of
                support they should be seeking when contacting this user. E.g.,
                &quot;
                {t("common:capitalise", { key: "generic.idea" })}{" "}
                submissions&quot; or &quot;General queries&quot;.
              </Subheader>
            </H3>
            <Input
              fluid
              value={updatingRole?.name}
              onChange={(_e, { value }) => {
                if (updatingRole)
                  setUpdatingRole({ ...updatingRole, name: value });
              }}
            />
            <H3>
              Description
              <Subheader>
                This description will also be shown to the user and gives them
                further direction of how to ask this point of contact for help.
              </Subheader>
            </H3>
            <Input
              fluid
              value={updatingRole?.description}
              onChange={(_e, { value }) => {
                if (updatingRole) {
                  setUpdatingRole({
                    ...updatingRole,
                    description: value as string,
                  });
                }
              }}
            />
          </ModalContent>
          <ModalActions>
            <Button
              onClick={() => {
                setUpdatingRole(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={applyRoleEdit} color="primary">
              {t("generic.done")}
            </Button>
          </ModalActions>
        </Modal>

        <H3>
          Point of contact users
          <Subheader>
            Choose users in your organisation to be a point of contact for
            support for other users in {ownerOrganisation.name}. For example,
            someone who can be contacted if a user needs help using the
            platform. Please note that the selected users email addresses will
            be available to other users in {ownerOrganisation.name}.
          </Subheader>
        </H3>

        <Table style={{ maxWidth: "100%" }}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell collapsing></TableHeaderCell>
              <TableHeaderCell collapsing></TableHeaderCell>
              <TableHeaderCell>Role</TableHeaderCell>
              <TableHeaderCell>Assigned to</TableHeaderCell>
              <TableHeaderCell></TableHeaderCell>
              <TableHeaderCell>Description</TableHeaderCell>
              <TableHeaderCell textAlign="right"></TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pointOfContactRoles.map((role, index) => (
              <TableRow key={role.id}>
                <TableCell textAlign="right">
                  {!role.assignedTo ? (
                    <Popup
                      trigger={
                        <Icon
                          icon={TriangleAlertIcon}
                          color="yellow"
                          size="large"
                        />
                      }
                      position="top center"
                    >
                      This role is currently not visible to your users because
                      it has not been assigned to a user.
                    </Popup>
                  ) : (
                    <Icon icon={CircleCheckIcon} color="green" size="large" />
                  )}
                </TableCell>
                <TableCell>
                  <div style={{ display: "flex" }}>
                    <Button
                      disabled={index === 0}
                      size="small"
                      onClick={() => {
                        api.organisations.reorderPointOfContactRole(
                          ownerOrganisation._id,
                          role.id,
                          "up",
                          ({ pointOfContactRoles: newRoles }) => {
                            setSavedRoles(newRoles);
                          },
                          () => {},
                        );
                      }}
                    >
                      <Icon icon={ArrowUpIcon} />
                    </Button>
                    <Button
                      disabled={index === pointOfContactRoles.length - 1}
                      size="small"
                      onClick={() => {
                        api.organisations.reorderPointOfContactRole(
                          ownerOrganisation._id,
                          role.id,
                          "down",
                          ({ pointOfContactRoles: newRoles }) => {
                            setSavedRoles(newRoles);
                          },
                          () => {},
                        );
                      }}
                    >
                      <Icon icon={ArrowDownIcon} />
                    </Button>
                  </div>
                </TableCell>
                <TableCell collapsing>{role.name}</TableCell>
                {role.assignedTo ? (
                  <>
                    <TableCell collapsing>
                      <UserChip user={role.ownerAssignedTo} />
                    </TableCell>
                    <TableCell collapsing>
                      <Button
                        size="small"
                        onClick={() => {
                          const { assignedTo } = role;
                          if (!assignedTo) return;
                          util
                            .confirm(
                              "Remove point of contact",
                              `Are you sure you want to remove "${role?.ownerAssignedTo?.profile?.fullName || "Deleted user"}" as the assigned contact for your users if the need help regarding "${role.name}"?`,
                            )
                            .then(() => {
                              api.organisations.unassignPointOfContact(
                                ownerOrganisation._id,
                                role.id,
                                assignedTo,
                                ({ pointOfContactRoles: newRoles }) => {
                                  setSavedRoles(newRoles);
                                },
                                () => {},
                              );
                            })
                            .catch(() => {});
                        }}
                      >
                        Unassign
                      </Button>
                    </TableCell>
                  </>
                ) : (
                  <TableCell colSpan={2} textAlign="right">
                    <UserChooser
                      single
                      trigger={<Button size="small">Assign user</Button>}
                      enabledFeatures={{ search: true }}
                      searchFunction={(query, success, failure) => {
                        if (!query) {
                          success([]);
                          return;
                        }
                        api.search.omni(
                          { query, include: ["users"] },
                          ({ users }) => users && success(users),
                          failure,
                        );
                      }}
                      onComplete={(users) => {
                        const selectedUser = users[0];
                        if (!selectedUser?._id) {
                          return;
                        }

                        api.organisations.assignPointOfContact(
                          orgId,
                          role.id,
                          selectedUser._id,
                          ({ pointOfContactRoles: newRoles }) => {
                            setSavedRoles(newRoles);
                          },
                          () => {},
                        );
                      }}
                    />
                  </TableCell>
                )}
                <TableCell
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 150,
                  }}
                >
                  {role.description}
                </TableCell>
                <TableCell textAlign="right" collapsing>
                  <div style={{ display: "flex" }}>
                    <Button
                      size="small"
                      onClick={() => {
                        setUpdatingRole({
                          id: role.id,
                          name: role.name,
                          description: role.description,
                        });
                      }}
                    >
                      <Icon icon={PencilIcon} />
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        util
                          .confirm(
                            "Delete point of contact role",
                            `Are you sure you want to delete the point of contact role "${role.name}"?`,
                          )
                          .then(() => {
                            api.organisations.deletePointOfContactRole(
                              ownerOrganisation._id,
                              role.id,
                              ({ pointOfContactRoles: newRoles }) => {
                                setSavedRoles(newRoles);
                              },
                              () => {},
                            );
                          })
                          .catch(() => {});
                      }}
                      variant="outline"
                    >
                      <Icon icon={Trash2Icon} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={7} textAlign="right">
                <Button
                  onClick={() => {
                    util
                      .prompt(
                        "Create point of contact role",
                        `Please give this point of contact a title. Make sure this describes the nature of support they should be seeking when contacting this user. E.g., "${t(
                          "common:capitalise",
                          { key: "generic.idea" },
                        )} submissions" or "General queries".`,
                        (roleName) => {
                          api.organisations.createPointOfContactRole(
                            orgId,
                            { name: roleName },
                            ({ pointOfContactRoles: newRoles }) => {
                              setSavedRoles(newRoles);
                            },
                            () => {},
                          );
                        },
                      )
                      .catch(() => {});
                  }}
                >
                  Create new role
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default PointOfContact;
